/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Col,
  Dropdown,
  Layout,
  MenuProps,
  Row,
  theme,
  Typography,
  App,
  Button
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CaretDownOutlined
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  logoutUser,
  watchAuthMessage,
  watchUser
} from '@/redux/slices/auth.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { useMsal } from '@azure/msal-react';

type HeaderProps = {
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
};
export const Header = ({ collapsed, setCollapsed }: HeaderProps) => {
  const {
    token: { colorBgContainer }
  } = theme.useToken();
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const { modal } = App.useApp();
  const messageInfo = useAppSelector(watchAuthMessage);
  const user = useAppSelector(watchUser);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    await instance.logout();
  };

  const confirmLogout = () => {
    modal.confirm({
      title: 'Xác nhận đăng xuất',
      content: 'Bạn có muốn đăng xuất không?',
      okText: 'Đăng xuất',
      cancelText: 'Hủy',
      onOk: handleLogout
    });
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Text onClick={confirmLogout}>Đăng xuất</Typography.Text>
      )
    }
  ];

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([{ type: messageInfo.type, message: messageInfo.message }])
      );
    }
  }, [dispatch, messageInfo]);

  return (
    <Layout.Header css={rootStyles(colorBgContainer, collapsed)}>
      <Row justify='space-between' align='middle'>
        <Col>
          <Button
            css={collapsedBtnStyles}
            type='text'
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
        </Col>
        <Col>
          <Dropdown
            trigger={['click']}
            menu={{ items }}
            placement='bottomRight'
            arrow
          >
            <Button type='text' icon={<CaretDownOutlined />} iconPosition='end'>
              {user?.fullName}
            </Button>
          </Dropdown>
        </Col>
      </Row>
    </Layout.Header>
  );
};

const rootStyles = (background: string, collapsed: boolean) => css`
  background: ${background};
  padding: 0;
  height: var(--header-height);
  position: fixed;
  right: 0;
  left: ${collapsed
    ? 'var(--collapsed-sidebar-width)'
    : 'var(--expanded-sidebar-width)'};
  transition: left 0.25s ease;
  border-bottom: 0.1rem solid var(--border-color);
  padding: 0 1rem 0 0.4rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  body.modal-open & {
    filter: blur(5px);
    pointer-events: none;
  }
  z-index: 99;
`;

const collapsedBtnStyles = css`
  font-size: 1.6rem;
  width: 2.4rem;
  height: 2.4rem;
  &:hover {
    background: none !important;
    opacity: 0.85;
  }
`;
